// app/javascript/controllers/organization_geocoder_controller.js
import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default class extends Controller {
  static targets = [
    "map", 
    "searchContainer", 
    "latitude", 
    "longitude", 
    "confirmed", 
    "geocodingStatus",
    "addressField",
    "postalCodeField",
    "cityField",
    "sousPrefectureField",
    "regionField",
    "country"
  ]
  
  static values = {
    mapboxApiKey: String
  }
  
  // Mapping des sous-préfectures vers codes postaux
  postalCodeMapping = {
    "Ali Adde": "77301",
    "Ali Sabieh": "77301", 
    "Holhol": "77301",
    "Arta": "77201",
    "As Eyla": "77401",
    "Dikhil": "77401",
    "Galafi": "77401",
    "Yoboki": "77401",
    "Djiboutii": "77101",
    "Adailou": "77501",
    "Dadda'to": "77501",
    "Khôr 'Angar": "77501",
    "Moulhoule": "77501",
    "Obock": "77501",
    "Balho": "77601",
    "Dorra": "77601",
    "Lac 'Assal": "77601",
    "Mousa Ali": "77601",
    "Randa": "77601",
    "Tadjoura": "77601"
  }
  
  // Mapping des régions vers codes postaux par défaut (quand sous-préfecture non disponible)
  regionPostalCodeMapping = {
    "Ali Sabieh": "77301",
    "Arta": "77201", 
    "Dikhil": "77401",
    "Djibouti": "77101",
    "Obock": "77501",
    "Tadjoura": "77601"
  }
  
  connect() {
    if (this.mapTarget && this.searchContainerTarget) {
      this.initializeMapbox();
    }
    
    // Écouter les changements sur le select sous-préfecture
    if (this.hasSousPrefectureFieldTarget) {
      this.sousPrefectureFieldTarget.addEventListener('change', this.handleSousPrefectureChange.bind(this));
    }
  }
  
  // Méthode pour gérer le changement de sous-préfecture
  handleSousPrefectureChange(event) {
    const selectedSousPrefecture = event.target.value;
    
    if (selectedSousPrefecture) {
      // Auto-remplir le code postal
      this.updatePostalCode(selectedSousPrefecture);
      
      // Auto-sélectionner la région si elle existe
      if (this.hasRegionFieldTarget) {
        const selectedOption = event.target.selectedOptions[0];
        if (selectedOption && selectedOption.text.includes('(')) {
          const regionMatch = selectedOption.text.match(/\(([^)]+)\)/);
          if (regionMatch) {
            const regionName = regionMatch[1];
            
            Array.from(this.regionFieldTarget.options).forEach(option => {
              if (option.value === regionName) {
                option.selected = true;
              }
            });
          }
        }
      }
    } else {
      // Vider le code postal si aucune sous-préfecture sélectionnée
      if (this.hasPostalCodeFieldTarget) {
        this.postalCodeFieldTarget.value = '';
      }
    }
  }
  
  // Nouvelle méthode pour mettre à jour le code postal
  updatePostalCode(sousPrefecture) {
    if (this.hasPostalCodeFieldTarget && this.postalCodeMapping[sousPrefecture]) {
      this.postalCodeFieldTarget.value = this.postalCodeMapping[sousPrefecture];
      
      // Ajouter une indication visuelle que le code postal a été auto-rempli
      this.postalCodeFieldTarget.classList.add('bg-green-50');
      setTimeout(() => {
        this.postalCodeFieldTarget.classList.remove('bg-green-50');
      }, 1000);
    }
  }
  
  initializeMapbox() {
    mapboxgl.accessToken = this.mapboxApiKeyValue;
    
    // Coordonnées par défaut (Centre de Djibouti)
    let defaultLng = 42.590275;
    let defaultLat = 11.8251386;
    let defaultZoom = 5;
    
    // Si des coordonnées existent déjà, les utiliser
    if (this.latitudeTarget.value && this.longitudeTarget.value) {
      defaultLat = parseFloat(this.latitudeTarget.value);
      defaultLng = parseFloat(this.longitudeTarget.value);
      defaultZoom = 14;
      this.confirmedTarget.value = "true";
    }
    
    // Initialiser la carte
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v11',
      center: [defaultLng, defaultLat],
      zoom: defaultZoom
    });
    
    // Ajouter les contrôles de navigation
    this.map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    
    // Créer un marqueur déplaçable
    this.marker = new mapboxgl.Marker({ draggable: true })
      .setLngLat([defaultLng, defaultLat])
      .addTo(this.map);
    
    // Mettre à jour les coordonnées quand le marqueur est déplacé
    this.marker.on('dragend', this.updateCoordinatesFromMarker.bind(this));
    
    // Initialiser le géocodeur pour la recherche d'adresse
    this.initializeGeocoder();
    
    // Activer le clic sur la carte pour déplacer le marqueur
    this.map.on('click', this.handleMapClick.bind(this));
  }
  
  initializeGeocoder() {
    // Configurer le géocodeur Mapbox
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: 'Rechercher une adresse...',
      countries: 'dj,fr',
      language: 'fr',
      types: 'address,place,locality',
      marker: false
    });
    
    // Ajouter le géocodeur au conteneur
    geocoder.addTo(this.searchContainerTarget);
    
    // Gérer la sélection d'un résultat
    geocoder.on('result', (event) => {
      const result = event.result;
      
      // Mettre à jour le marqueur et la carte
      const [lng, lat] = result.center;
      this.marker.setLngLat([lng, lat]);
      this.map.flyTo({
        center: [lng, lat],
        zoom: 15
      });
      
      // Extraire et remplir les champs d'adresse
      this.populateAddressFields(result);
      
      // Mettre à jour les coordonnées
      this.updateCoordinates(lat, lng);
      
      // Marquer comme confirmé
      this.confirmedTarget.value = "true";
      
      // Mettre à jour le statut
      this.geocodingStatusTarget.textContent = `Adresse géolocalisée: ${result.place_name}`;
      this.geocodingStatusTarget.classList.remove("text-red-500");
      this.geocodingStatusTarget.classList.add("text-green-500");
    });
  }
  
  populateAddressFields(result) {
    // Réinitialiser tous les champs avant de les remplir
    this.resetAllFields();
    
    let address = '';
    let postalCode = '';
    let city = '';
    let region = '';
    let sousPrefecture = '';
    let country = '';
    
    // Extraire les informations du contexte
    if (result.context) {
      // Code postal
      const postalCodeContext = result.context.find(item => item.id.startsWith('postcode'));
      if (postalCodeContext) {
        postalCode = postalCodeContext.text;
      }
      
      // Ville
      const placeContext = result.context.find(item => item.id.startsWith('place'));
      if (placeContext) {
        city = placeContext.text;
      }
      
      // Région (admin1)
      const regionContext = result.context.find(item => item.id.startsWith('region'));
      if (regionContext) {
        region = regionContext.text;
      }
      
      // Sous-préfecture/District (admin2)
      const districtContext = result.context.find(item => item.id.startsWith('district'));
      if (districtContext) {
        sousPrefecture = districtContext.text;
      }
      
      // Pays
      const countryContext = result.context.find(item => item.id.startsWith('country'));
      if (countryContext) {
        country = countryContext.text;
      }
    }
    
    // Si le résultat est lui-même une ville, utiliser son nom
    if (result.place_type.includes('place')) {
      city = result.text;
    }
    
    // Pour l'adresse, utiliser le texte principal si c'est une adresse
    if (result.place_type.includes('address')) {
      address = result.text;
    } else if (!result.place_type.includes('place')) {
      const parts = result.place_name.split(',');
      if (parts.length > 0) {
        address = parts[0].trim();
      }
    }
    
    // Remplir les champs
    if (this.hasAddressFieldTarget && address) {
      this.addressFieldTarget.value = address;
    }
    
    // Pour le code postal, priorité au mapping local
    if (this.hasPostalCodeFieldTarget) {
      if (sousPrefecture && this.postalCodeMapping[sousPrefecture]) {
        // Sous-préfecture trouvée et mappée
        this.postalCodeFieldTarget.value = this.postalCodeMapping[sousPrefecture];
      } else if (region && this.regionPostalCodeMapping[region]) {
        // Pas de sous-préfecture, mais région trouvée
        this.postalCodeFieldTarget.value = this.regionPostalCodeMapping[region];
      } else if (postalCode) {
        // Utiliser le code postal fourni par Mapbox en dernier recours
        this.postalCodeFieldTarget.value = postalCode;
      }
    }
    
    if (this.hasCityFieldTarget && city) {
      this.cityFieldTarget.value = city;
    }
    
    // Pour les selects, chercher l'option correspondante
    if (this.hasSousPrefectureFieldTarget && sousPrefecture) {
      Array.from(this.sousPrefectureFieldTarget.options).forEach(option => {
        if (option.value === sousPrefecture) {
          option.selected = true;
          // Déclencher le changement pour auto-sélectionner la région et le code postal
          this.handleSousPrefectureChange({ target: this.sousPrefectureFieldTarget });
        }
      });
    }
    
    if (this.hasRegionFieldTarget && region && !sousPrefecture) {
      // Ne sélectionner la région que si aucune sous-préfecture n'a été trouvée
      Array.from(this.regionFieldTarget.options).forEach(option => {
        if (option.value === region) {
          option.selected = true;
        }
      });
    }
    
    if (this.hasCountryTarget && country) {
      this.countryTarget.value = country;
    }
  }
  
  // Nouvelle méthode pour réinitialiser tous les champs
  resetAllFields() {
    // Réinitialiser les champs texte
    if (this.hasAddressFieldTarget) {
      this.addressFieldTarget.value = '';
    }
    
    if (this.hasPostalCodeFieldTarget) {
      this.postalCodeFieldTarget.value = '';
    }
    
    if (this.hasCityFieldTarget) {
      this.cityFieldTarget.value = '';
    }
    
    if (this.hasCountryTarget) {
      this.countryTarget.value = '';
    }
    
    // Réinitialiser les selects (remettre sur l'option par défaut)
    if (this.hasSousPrefectureFieldTarget) {
      this.sousPrefectureFieldTarget.selectedIndex = 0; // Première option (prompt)
    }
    
    if (this.hasRegionFieldTarget) {
      this.regionFieldTarget.selectedIndex = 0; // Première option (prompt)
    }
  }
  
  handleMapClick(event) {
    const { lng, lat } = event.lngLat;
    this.marker.setLngLat([lng, lat]);
    this.updateCoordinates(lat, lng);
    
    // Mettre à jour le statut
    this.geocodingStatusTarget.textContent = "Position ajustée manuellement";
    this.geocodingStatusTarget.classList.remove("text-red-500");
    this.geocodingStatusTarget.classList.add("text-green-500");
    
    // Marquer comme confirmé
    this.confirmedTarget.value = "true";
    
    // Essayer de faire une géocodification inverse pour obtenir l'adresse
    this.reverseGeocode(lng, lat);
  }
  
  updateCoordinatesFromMarker() {
    const lngLat = this.marker.getLngLat();
    this.updateCoordinates(lngLat.lat, lngLat.lng);
    
    // Mettre à jour le statut
    this.geocodingStatusTarget.textContent = "Position ajustée manuellement";
    this.geocodingStatusTarget.classList.remove("text-red-500");
    this.geocodingStatusTarget.classList.add("text-green-500");
    
    // Essayer de faire une géocodification inverse pour obtenir l'adresse
    this.reverseGeocode(lngLat.lng, lngLat.lat);
  }
  
  updateCoordinates(lat, lng) {
    this.latitudeTarget.value = parseFloat(lat).toFixed(6);
    this.longitudeTarget.value = parseFloat(lng).toFixed(6);
    this.confirmedTarget.value = "true";
  }
  
  reverseGeocode(lng, lat) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${this.mapboxApiKeyValue}&language=fr&types=address&limit=1`;
    
    fetch(url)
      .then(response => response.json())
      .then(data => {
        if (data.features && data.features.length > 0) {
          const result = data.features[0];
          this.populateAddressFields(result);
        }
      })
      .catch(error => console.error('Erreur lors de la géocodification inverse:', error));
  }
}